import dayjs, { Dayjs } from "dayjs";
import * as UTC from "dayjs/plugin/utc";
import * as CustomParseFormat from "dayjs/plugin/customParseFormat";
import i18next from "i18next";
import { HauntedFunc } from "Shared/haunted/HooksHelpers";
import { html, useEffect } from "haunted";
import { useState } from "Shared/haunted/CustomHooks";
import { createHeaders } from "ComponentHelpers/HeaderHelper";
import { API_AMERICAN_AIRLINES_REFRESH_TOKEN_URL, ROLE_CODES } from "Services/constants";
import { useReduxState } from "Shared/redux/useReduxState";
import { logOutInline } from "Services/common";
import { useManualCountdown } from "./useManualCountDown";
import { iconButton } from "Components/iconButton";
dayjs.extend(CustomParseFormat);
dayjs.extend(UTC);

export const name = "ac-american-airlines-token-modal";
export const useShadowDOM = false;

let countdownWorker: Worker | null = null;

export const Component: HauntedFunc<{}> = () => {
	const [userInfo] = useReduxState("userInfo");

	const finalCountdown = useManualCountdown();

	const [mode, setMode] = useState<"default" | "counting" | "ending" | "ended" | "error">("default");
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const init = async () => {
		if (!userInfo?.AmericanAirlines?.IsMember || userInfo?.RoleCode !== ROLE_CODES.AMERICAN_AIRLINES_USER) {
			return;
		}

		const deadline = userInfo?.AmericanAirlines?.TokenValidTo;

		if (!deadline) {
			setMode("ended");
			return;
		}

		startCountdown(deadline);
	};

	const parseTime = (time: string): Dayjs => {
		const retVal = dayjs.utc(time, "YYYY-MM-DD HH:mm:ss");
		return retVal;
	};

	const isOpen = mode !== "default" && mode !== "counting";
	const canRefresh = mode !== "ended" && mode !== "error";

	const startCountdown = (deadline: string) => {
		if (countdownWorker) {
			countdownWorker.terminate();
		}

		countdownWorker = new Worker("/web_workers/AmericanSessionCounterWorker.js");

		countdownWorker.onmessage = (e) => {
			if (e.data.type === "START_COUNTDOWN") {
				finalCountdown.startCountdown(e.data.remainingTime);
				setMode("ending");
			}

			if (e.data.type === "SESSION_ENDED") {
				finalCountdown.startCountdown(e.data.remainingTime);
				setMode("ended");
			}
		};

		const targetUtcTime = parseTime(deadline).valueOf();

		countdownWorker.postMessage({ targetTime: targetUtcTime });

		setMode("counting");
	};

	const refreshSession = async () => {
		const headers = createHeaders();

		const response = await fetch(
			`${window.JetSmart.Settings.BookingUrl}${API_AMERICAN_AIRLINES_REFRESH_TOKEN_URL}`,
			{
				method: "GET",
				headers,
				mode: "cors",
				credentials: "include",
			}
		);

		if (!response.ok) {
			setMode("error");
			return;
		}

		const responseJson = await response.json();
		setIsLoading(false);
		startCountdown(responseJson.data);
	};

	const handleContinueButtonClick = async () => {
		setIsLoading(true);
		await refreshSession();
	};

	const handleRestartButtonClick = async () => {
		try {
			await logOutInline();
			window.location.href = "/";
		} catch (err) {
			// DEVNOTE: This is a fallback in case the server does not recognize the call or the user is already logged out.
			window.location.replace("/");
		}
	};

	const refreshButton = iconButton({
		isAction: true,
		icon: html`<i class="jsh-circle-chevron-right1 text-base"></i>`,
		label: i18next.t("Session-RefreshButton"),
		isDisabled: isLoading,
		onClick: handleContinueButtonClick,
	});

	const restartButton = iconButton({
		isAction: true,
		icon: html`<i class="jsh-circle-chevron-right1 text-base"></i>`,
		label: i18next.t("Session-StartOverButton"),
		onClick: handleRestartButtonClick,
	});

	useEffect(async () => {
		if (finalCountdown.isCountdownFinished && isOpen && userInfo?.AmericanAirlines?.IsMember) {
			setMode("ended");
			await logOutInline();
		}
	}, [finalCountdown.isCountdownFinished, isOpen, userInfo?.RoleCode]);

	useEffect(init, [userInfo?.RoleCode]);

	const sessionWarningMessageTemplate = () => (canRefresh ? html` <div>${i18next.t("Session-Warning2")}</div> ` : "");

	const sessionRefreshButtonTemplate = () => (canRefresh ? refreshButton.htmlTemplate() : "");

	const errorWarningTemplate = () =>
		mode === "error" ? html` <div>${i18next.t("Ha ocurrido un error.")}</div> ` : "";

	const sessionExpiredMessageTemplate = () =>
		mode === "ended" ? html` <div>${i18next.t("Session-Expired")}</div> ` : "";

	const startOverButtonTemplate = () => (!canRefresh ? restartButton.htmlTemplate() : "");

	const timerDisplayTemplate = () => {
		if (mode === "error") return "";
		if (mode === "ended") return html` <span class="ml-2">(00:00)</span>`;

		const remainingMinutes = !isNaN(finalCountdown.remainingMinutes)
			? finalCountdown.remainingMinutes.toString().padStart(2, "0")
			: "00";
		const remainingSeconds = !isNaN(finalCountdown.remainingSeconds)
			? finalCountdown.remainingSeconds.toString().padStart(2, "0")
			: "00";

		return html` <span class="ml-2">(${remainingMinutes}:${remainingSeconds})</span> `;
	};

	const headerTemplate = () => html`
		<div
			class="flex items-center justify-start rounded-lg bg-[#def3f7] p-5 text-left text-[22px] font-black text-[#163a70]"
		>
			<i class="fas fa-exclamation-circle mr-5 text-[36px] text-[#59c3d9]"></i>
			${i18next.t("Session-Warning")} ${timerDisplayTemplate()}
		</div>
	`;

	return isOpen
		? html`
				<div class="fixed inset-0 z-[100001] bg-n-blue bg-opacity-75 backdrop-blur-sm">
					<div class="absolute !w-[500px] max-w-[95%] overflow-hidden rounded-2xl bg-white p-3 all-center">
						${headerTemplate()}
						<div class="p-5">
							${sessionWarningMessageTemplate()} ${sessionExpiredMessageTemplate()}
							${errorWarningTemplate()}
							<div class="mt-[30px] flex w-full items-center justify-end">
								${sessionRefreshButtonTemplate()} ${startOverButtonTemplate()}
							</div>
						</div>
					</div>
				</div>
		  `
		: html``;
};
